import { useState, useRef, useEffect } from 'react';
import { DotsThree, PencilSimple, Trash, Flag } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

interface PostCardMenuProps {
  postId: string;
  isAuthor: boolean;
  onDelete: () => void;
  onReport: () => void;
}

export default function PostCardMenu({ postId, isAuthor, onDelete, onReport }: PostCardMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
        aria-label="Post options"
      >
        <DotsThree size={20} weight="bold" />
      </button>

      {open && (
        <div className="absolute right-0 top-8 w-36 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
          {isAuthor ? (
            <>
              <Link
                to={`/community/post/${postId}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 w-full"
              >
                <PencilSimple size={16} />
                Edit
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onDelete(); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left"
              >
                <Trash size={16} />
                Delete
              </button>
            </>
          ) : (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onReport(); }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950 w-full text-left"
            >
              <Flag size={16} />
              Report
            </button>
          )}
        </div>
      )}
    </div>
  );
}
